import { useState, useEffect, useMemo } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { RestockAlerts } from "../components/forecast/RestockAlerts";
import { ForecastHistory } from "../components/forecast/ForecastHistory";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  Loader2, Sparkles, TrendingUp, ShoppingCart, AlertCircle, Package,
  Plus, Minus, History, ClipboardList, Check, Search, Trash2,
  Clock, X, ChevronDown, ChevronUp, UtensilsCrossed, Calendar, Flame, Sun, Gift, Zap
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatCurrency, formatDateTime } from "../lib/utils";

const periods = [
  { value: "day",     label: "Un día" },
  { value: "weekend", label: "Fin de semana" },
  { value: "week",    label: "Una semana" },
  { value: "month",   label: "Un mes" },
];

const periodLabel = (v) => periods.find(p => p.value === v)?.label || v;

// ── History Period Selector ─────────────────────────────────────────
function HistoryPeriodSelector({ currentRestaurant, period, onForecast, forecasting }) {
  const [presetType, setPresetType] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [periodSummary, setPeriodSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [peakData, setPeakData] = useState(null);
  const [loadingPeak, setLoadingPeak] = useState(false);

  const now = new Date();
  const lastYear = now.getFullYear() - 1;

  const presets = [
    { key: "all", label: "Todo el historial", icon: History, desc: "Todas las ventas registradas" },
    { key: "peak_week", label: "Semana pico", icon: Flame, desc: `Semana con mas ventas en ${lastYear}` },
    { key: "peak_month", label: "Mes pico", icon: Zap, desc: `Mes con mas ventas en ${lastYear}` },
    { key: "summer", label: `Verano ${lastYear}`, icon: Sun, desc: `Jun-Sep ${lastYear}`, from: `${lastYear}-06-01`, to: `${lastYear}-09-30` },
    { key: "christmas", label: `Navidades ${lastYear}`, icon: Gift, desc: `Dic ${lastYear}`, from: `${lastYear}-12-01`, to: `${lastYear}-12-31` },
    { key: "custom", label: "Rango personalizado", icon: Calendar, desc: "Elige fechas" },
  ];

  useEffect(() => {
    if (presetType === "peak_week" || presetType === "peak_month") {
      if (!peakData) {
        setLoadingPeak(true);
        api.getPeakSalesPeriod(currentRestaurant.restaurant_id)
          .then(data => { setPeakData(data); applyPeakDates(data, presetType); })
          .catch(() => toast.error("Error al detectar pico de ventas"))
          .finally(() => setLoadingPeak(false));
      } else {
        applyPeakDates(peakData, presetType);
      }
    } else if (presetType === "all") {
      setDateFrom("");
      setDateTo("");
      setPeriodSummary(null);
    } else {
      const preset = presets.find(p => p.key === presetType);
      if (preset?.from) {
        setDateFrom(preset.from);
        setDateTo(preset.to);
      }
    }
  }, [presetType]); // eslint-disable-line

  const applyPeakDates = (data, type) => {
    const source = type === "peak_week" ? data?.peak_week : data?.peak_month;
    if (source) {
      setDateFrom(source.date_from);
      setDateTo(source.date_to);
    } else {
      toast.error("No hay datos de ventas del ano pasado");
      setPresetType("all");
    }
  };

  useEffect(() => {
    if (!dateFrom || !dateTo) { setPeriodSummary(null); return; }
    setLoadingSummary(true);
    api.getPeriodSalesSummary(currentRestaurant.restaurant_id, dateFrom, dateTo)
      .then(setPeriodSummary)
      .catch(() => setPeriodSummary(null))
      .finally(() => setLoadingSummary(false));
  }, [dateFrom, dateTo]); // eslint-disable-line

  const activePreset = presets.find(p => p.key === presetType);
  const peakInfo = presetType === "peak_week" ? peakData?.peak_week
    : presetType === "peak_month" ? peakData?.peak_month : null;

  return (
    <Card className="border-none shadow-soft" data-testid="history-period-selector">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2 font-medium">
          <History className="w-4 h-4 text-primary" />
          Prevision basada en historial de ventas
        </div>

        {/* Preset chips */}
        <div className="flex flex-wrap gap-2" data-testid="period-presets">
          {presets.map(p => {
            const Icon = p.icon;
            return (
              <button
                key={p.key}
                onClick={() => setPresetType(p.key)}
                data-testid={`preset-${p.key}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all border ${
                  presetType === p.key
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "bg-muted/50 hover:bg-muted border-transparent"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Peak info badge */}
        {loadingPeak && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Analizando ventas del ano pasado...
          </div>
        )}
        {peakInfo && (presetType === "peak_week" || presetType === "peak_month") && (
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              <Flame className="w-4 h-4 inline mr-1" />
              {presetType === "peak_month" ? peakInfo.label : peakInfo.key}
              {" — "}{formatCurrency(peakInfo.revenue)} en {peakInfo.tickets} tickets
            </p>
          </div>
        )}

        {/* Custom date range */}
        {presetType === "custom" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Hasta</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="date-to" />
            </div>
          </div>
        )}

        {/* Period summary */}
        {loadingSummary && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando datos del periodo...
          </div>
        )}
        {periodSummary && !loadingSummary && (
          <div className="bg-muted/50 rounded-xl p-4 space-y-3" data-testid="period-summary">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
              <Calendar className="w-3.5 h-3.5" />
              Resumen del periodo ({periodSummary.num_days} dias)
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-background rounded-lg p-3 text-center border">
                <div className="font-bold text-lg font-mono text-primary">{formatCurrency(periodSummary.total_revenue)}</div>
                <div className="text-xs text-muted-foreground">Facturacion total</div>
              </div>
              <div className="bg-background rounded-lg p-3 text-center border">
                <div className="font-bold text-lg font-mono">{periodSummary.total_tickets}</div>
                <div className="text-xs text-muted-foreground">Tickets</div>
              </div>
              <div className="bg-background rounded-lg p-3 text-center border">
                <div className="font-bold text-lg font-mono">{formatCurrency(periodSummary.avg_daily_revenue)}</div>
                <div className="text-xs text-muted-foreground">Media diaria</div>
              </div>
              <div className="bg-background rounded-lg p-3 text-center border">
                <div className="font-bold text-lg font-mono">{periodSummary.avg_daily_tickets}</div>
                <div className="text-xs text-muted-foreground">Tickets/dia</div>
              </div>
            </div>
            {/* Top dishes in period */}
            {periodSummary.top_dishes?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">Top platos del periodo</p>
                <div className="flex flex-wrap gap-1.5">
                  {periodSummary.top_dishes.slice(0, 8).map((d, i) => (
                    <Badge key={i} variant="secondary" className="text-xs font-mono gap-1">
                      {d.name} <span className="text-muted-foreground">x{d.quantity}</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Generate button */}
        <div className="flex justify-end">
          <Button
            onClick={() => onForecast(dateFrom || null, dateTo || null, periodSummary)}
            disabled={forecasting || (presetType === "custom" && (!dateFrom || !dateTo))}
            className="rounded-full gap-2"
            data-testid="generate-history-forecast-btn"
          >
            {forecasting
              ? <><Loader2 className="w-4 h-4 animate-spin" />Calculando...</>
              : <><Sparkles className="w-4 h-4" />Generar prevision</>
            }
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Dish thumbnail card ────────────────────────────────────────────────
function DishCard({ dish, selected, onAdd, onUpdateQty }) {
  return (
    <button
      type="button"
      onClick={() => !selected && onAdd(dish.dish_id)}
      data-testid={`dish-card-${dish.dish_id}`}
      className={`relative rounded-xl text-left transition-all overflow-hidden border-2 w-full ${
        selected
          ? "border-primary shadow-md bg-primary/5"
          : "border-transparent bg-muted/30 hover:bg-muted hover:border-border"
      }`}
    >
      {/* Thumbnail */}
      <div className="aspect-square w-full overflow-hidden bg-muted">
        {dish.image_url ? (
          <img
            src={dish.image_url}
            alt={dish.name}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
            <UtensilsCrossed className="w-7 h-7 text-primary/25" />
          </div>
        )}
      </div>

      {/* Selected checkmark */}
      {selected && (
        <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow">
          <Check className="w-3 h-3 text-white" />
        </div>
      )}

      {/* Info */}
      <div className="p-2.5">
        <div className="font-medium text-xs leading-snug line-clamp-2 min-h-[2.5rem]">{dish.name}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{formatCurrency(dish.price)}</div>
      </div>

      {/* Quantity controls */}
      {selected && (
        <div
          className="flex items-center justify-center gap-2 pb-2.5"
          onClick={e => e.stopPropagation()}
        >
          <button
            className="w-6 h-6 rounded-full bg-primary/20 hover:bg-primary/40 flex items-center justify-center transition-colors"
            onClick={() => onUpdateQty(dish.dish_id, -1)}
            data-testid={`dish-minus-${dish.dish_id}`}
          >
            <Minus className="w-3 h-3 text-primary" />
          </button>
          <span className="w-6 text-center font-bold text-sm text-primary">{selected.quantity}</span>
          <button
            className="w-6 h-6 rounded-full bg-primary/20 hover:bg-primary/40 flex items-center justify-center transition-colors"
            onClick={() => onUpdateQty(dish.dish_id, 1)}
            data-testid={`dish-plus-${dish.dish_id}`}
          >
            <Plus className="w-3 h-3 text-primary" />
          </button>
        </div>
      )}
    </button>
  );
}

// ── Dish Picker ────────────────────────────────────────────────────────
function DishPicker({ dishes, selectedDishes, onAdd, onUpdateQty, onClearAll }) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() =>
    dishes.filter(d => d.name.toLowerCase().includes(search.toLowerCase())),
    [dishes, search]
  );

  return (
    <div className="space-y-4">
      {/* Selected chips */}
      {selectedDishes.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center p-3 bg-primary/5 rounded-xl border border-primary/10">
          <span className="text-xs font-medium text-primary/70 mr-1">Seleccionados:</span>
          {selectedDishes.map(sd => {
            const dish = dishes.find(d => d.dish_id === sd.dish_id);
            return (
              <div
                key={sd.dish_id}
                className="flex items-center gap-1 bg-card dark:bg-background border border-primary/20 text-primary rounded-full pl-1 pr-2 py-0.5 text-xs font-medium shadow-sm"
                data-testid={`selected-chip-${sd.dish_id}`}
              >
                {dish?.image_url ? (
                  <img src={dish.image_url} alt={dish.name || "Plato"} className="w-4 h-4 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                    <UtensilsCrossed className="w-2.5 h-2.5 text-primary" />
                  </div>
                )}
                <span className="max-w-[80px] truncate">{sd.name}</span>
                <span className="text-primary/50 font-mono">×{sd.quantity}</span>
                <button
                  onClick={() => onUpdateQty(sd.dish_id, -sd.quantity)}
                  className="ml-0.5 hover:text-destructive transition-colors"
                  data-testid={`remove-chip-${sd.dish_id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          <button
            onClick={onClearAll}
            className="text-xs text-muted-foreground hover:text-destructive underline ml-1 transition-colors"
            data-testid="clear-all-btn"
          >
            Limpiar todo
          </button>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-2 border rounded-xl px-3 py-2.5 bg-background focus-within:ring-2 focus-within:ring-primary/20 transition-all">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          placeholder="Buscar plato..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          data-testid="dish-search-input"
        />
        {search && (
          <button onClick={() => setSearch("")} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {search ? `Sin resultados para "${search}"` : "No hay platos disponibles"}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3">
          {filtered.map(dish => (
            <DishCard
              key={dish.dish_id}
              dish={dish}
              selected={selectedDishes.find(d => d.dish_id === dish.dish_id)}
              onAdd={onAdd}
              onUpdateQty={onUpdateQty}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────
export default function ForecastPage() {
  const { currentRestaurant } = useOutletContext();
  const navigate = useNavigate();

  const [dishes, setDishes] = useState([]);
  const [salesSummary, setSalesSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forecasting, setForecasting] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [forecastDishesSnapshot, setForecastDishesSnapshot] = useState([]);

  const [period, setPeriod] = useState("week");
  const [selectedDishes, setSelectedDishes] = useState([]);
  const [forecastMode, setForecastMode] = useState("manual");
  const [historyOpen, setHistoryOpen] = useState(false);

  const [adjustments, setAdjustments] = useState({});
  const [selectedIngs, setSelectedIngs] = useState(new Set());
  const [adjustStep, setAdjustStep] = useState(0.1);

  const [savedForecasts, setSavedForecasts] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listName, setListName] = useState("");
  const [listType, setListType] = useState("shopping");

  useEffect(() => {
    if (!currentRestaurant) return;
    fetchData();
    fetchHistory();
  }, [currentRestaurant]); // eslint-disable-line

  const fetchData = async () => {
    try {
      const [menusData, summaryData] = await Promise.all([
        api.getMenus(currentRestaurant.restaurant_id),
        api.getSalesSummary(currentRestaurant.restaurant_id, 30).catch(() => null),
      ]);
      setSalesSummary(summaryData);
      const allDishes = [];
      for (const menu of menusData) {
        const menuDishes = await api.getDishes(menu.menu_id);
        allDishes.push(...menuDishes);
      }
      setDishes(allDishes);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const data = await api.getSavedForecasts(currentRestaurant.restaurant_id);
      setSavedForecasts(data);
    } catch { /* ignore */ }
    finally { setLoadingHistory(false); }
  };

  const addDish = (dishId) => {
    const dish = dishes.find(d => d.dish_id === dishId);
    if (!dish) return;
    setSelectedDishes(prev => {
      const existing = prev.find(d => d.dish_id === dishId);
      if (existing) return prev.map(d => d.dish_id === dishId ? { ...d, quantity: d.quantity + 1 } : d);
      return [...prev, { dish_id: dishId, name: dish.name, quantity: 1 }];
    });
  };

  const updateQty = (dishId, change) => {
    setSelectedDishes(prev =>
      prev.map(d => {
        if (d.dish_id !== dishId) return d;
        const q = Math.max(0, d.quantity + change);
        return q === 0 ? null : { ...d, quantity: q };
      }).filter(Boolean)
    );
  };

  const handleForecast = async () => {
    if (forecastMode === "manual" && selectedDishes.length === 0) {
      toast.error("Selecciona al menos un plato");
      return;
    }
    setForecasting(true);
    setForecastDishesSnapshot(
      selectedDishes.map(sd => ({
        ...sd,
        image_url: dishes.find(d => d.dish_id === sd.dish_id)?.image_url || null,
      }))
    );
    try {
      const dishesData = forecastMode === "manual"
        ? selectedDishes.map(d => ({ [d.dish_id]: d.quantity }))
        : [];

      const result = await api.aiForecast(currentRestaurant.restaurant_id, {
        period,
        dishes: dishesData,
        based_on_history: forecastMode === "history",
      });

      setForecast(result);
      setAdjustments({});
      setSelectedIngs(new Set());

      const dishesSummary = forecastMode === "manual"
        ? selectedDishes.map(d => `${d.name} (x${d.quantity})`)
        : result.historical_data?.slice(0, 5).map(h => h.dish_name) || [];

      const savedName = `Prevision ${periodLabel(period)} · ${new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}`;
      await api.saveForecast(currentRestaurant.restaurant_id, {
        name: savedName,
        period,
        mode: forecastMode,
        forecast_data: result,
        total_estimated_cost: result.total_estimated_cost || 0,
        ingredients_count: result.ingredients_needed?.length || 0,
        dishes_summary: dishesSummary,
      }).catch(() => {});

      await fetchHistory();
      toast.success("Prevision generada y guardada");

      setTimeout(() => {
        document.getElementById("forecast-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error(err.message || "Error al generar prevision");
    } finally {
      setForecasting(false);
    }
  };

  const handleHistoryForecast = async (dateFrom, dateTo, periodSummary) => {
    setForecasting(true);
    setForecastDishesSnapshot([]);
    try {
      const result = await api.aiForecast(currentRestaurant.restaurant_id, {
        period,
        dishes: [],
        based_on_history: true,
        date_from: dateFrom,
        date_to: dateTo,
      });

      setForecast(result);
      setAdjustments({});
      setSelectedIngs(new Set());

      const dishesSummary = result.historical_data?.slice(0, 5).map(h => h.dish_name) || [];
      const label = dateFrom && dateTo
        ? `${new Date(dateFrom).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })} - ${new Date(dateTo).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}`
        : "Todo el historial";
      const savedName = `Prevision ${periodLabel(period)} (${label})`;
      await api.saveForecast(currentRestaurant.restaurant_id, {
        name: savedName,
        period,
        mode: "history",
        forecast_data: result,
        total_estimated_cost: result.total_estimated_cost || 0,
        ingredients_count: result.ingredients_needed?.length || 0,
        dishes_summary: dishesSummary,
      }).catch(() => {});

      await fetchHistory();
      toast.success("Prevision generada y guardada");
      setTimeout(() => {
        document.getElementById("forecast-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error(err.message || "Error al generar prevision");
    } finally {
      setForecasting(false);
    }
  };

  const loadSavedForecast = async (fc) => {
    try {
      const full = await api.getSavedForecast(fc.forecast_id);
      setForecast(full.forecast_data);
      setPeriod(full.period);
      setForecastMode(full.mode);
      setAdjustments({});
      setSelectedIngs(new Set());
      setForecastDishesSnapshot([]);
      toast.success(`Cargada: ${full.name}`);
      setTimeout(() => {
        document.getElementById("forecast-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch {
      toast.error("Error al cargar previsión");
    }
  };

  const deleteHistory = async (id, e) => {
    e.stopPropagation();
    await api.deleteSavedForecast(id).catch(() => {});
    setSavedForecasts(prev => prev.filter(f => f.forecast_id !== id));
  };

  const displayIngredients = useMemo(() => {
    if (!forecast?.ingredients_needed) return [];
    return forecast.ingredients_needed.map((ing, idx) => {
      const adj = adjustments[idx] ?? 0;
      const basePurchase = ing.to_purchase ?? 0;
      return {
        ...ing,
        idx,
        display_to_purchase: Math.max(0, +(basePurchase + adj).toFixed(4)),
        display_quantity:    ing.total_quantity ?? 0,
        display_with_waste:  ing.quantity_with_waste ?? 0,
        adjustment: adj,
      };
    });
  }, [forecast, adjustments]);

  const applyAdjustment = (sign) => {
    if (!forecast?.ingredients_needed) return;
    const targets = selectedIngs.size > 0
      ? [...selectedIngs]
      : displayIngredients.map((_, i) => i);
    setAdjustments(prev => {
      const next = { ...prev };
      targets.forEach(idx => {
        const basePurchase = forecast.ingredients_needed[idx]?.to_purchase ?? 0;
        const current = prev[idx] ?? 0;
        const newVal = current + sign * adjustStep;
        next[idx] = Math.max(-basePurchase, +newVal.toFixed(4));
      });
      return next;
    });
  };

  const toggleIngSelection = (idx) => {
    setSelectedIngs(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const toggleAllIngs = (checked) => {
    setSelectedIngs(checked
      ? new Set(displayIngredients.map((_, i) => i))
      : new Set()
    );
  };

  const handleCreateShoppingList = async () => {
    if (!listName.trim()) { toast.error("Introduce un nombre"); return; }
    const itemsToBuy = displayIngredients.filter(ing => ing.display_to_purchase > 0);
    if (!itemsToBuy.length) {
      toast.error("No hay ingredientes que necesiten reposición");
      return;
    }
    setCreatingList(true);
    try {
      const items = itemsToBuy.map(ing => ({
        ingredient_name: ing.name,
        quantity: ing.display_to_purchase,
        unit: ing.unit,
        estimated_cost: ing.estimated_cost || 0,
        ingredient_id: null,
        supplier_id: null,
      }));
      await api.createShoppingList(currentRestaurant.restaurant_id, {
        name: listName,
        items,
        forecast_period: period,
        list_type: listType,
        notes: `Generada desde previsión de ${periodLabel(period)}`,
      });
      toast.success(`Lista creada con ${items.length} ingrediente(s) a reponer`);
      setListDialogOpen(false);
      setListName("");
      navigate("/app/shopping-lists");
    } catch { toast.error("Error al crear lista"); }
    finally { setCreatingList(false); }
  };

  const totalRations = selectedDishes.reduce((s, d) => s + d.quantity, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="forecast-page">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <PageHeader
        icon={<Sparkles className="w-5 h-5" />}
        title="Previsiones con IA"
        description="Calcula necesidades de materia prima por período."
        breadcrumb={[{ label: "Finanzas y análisis" }, { label: "Previsiones IA" }]}
      />

      {/* ── Config bar ──────────────────────────────────────────────── */}
      <Card className="border-none shadow-soft">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="space-y-1.5 flex-1">
              <Label className="text-xs text-muted-foreground">Método</Label>
              <Tabs value={forecastMode} onValueChange={v => { setForecastMode(v); setSelectedDishes([]); }}>
                <TabsList className="h-9">
                  <TabsTrigger value="manual" className="text-xs gap-1.5 px-4" data-testid="mode-manual">
                    <Plus className="w-3.5 h-3.5" /> Manual
                  </TabsTrigger>
                  <TabsTrigger value="history" className="text-xs gap-1.5 px-4" data-testid="mode-history">
                    <History className="w-3.5 h-3.5" /> Por historial de ventas
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Período</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="h-9 w-[160px]" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {periods.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Manual: Dish Picker ─────────────────────────────────────── */}
      {forecastMode === "manual" && (
        <Card className="border-none shadow-soft">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base flex items-center justify-between">
              <span>Seleccionar platos</span>
              <span className="text-xs font-normal text-muted-foreground">
                {dishes.length} platos disponibles
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <DishPicker
              dishes={dishes}
              selectedDishes={selectedDishes}
              onAdd={addDish}
              onUpdateQty={updateQty}
              onClearAll={() => setSelectedDishes([])}
            />

            {/* Generate bar */}
            <div className={`mt-4 p-3 rounded-xl border flex items-center justify-between gap-4 transition-all ${
              selectedDishes.length > 0
                ? "bg-primary/5 border-primary/20"
                : "bg-muted/30 border-border"
            }`}>
              <div className="text-sm">
                {selectedDishes.length > 0 ? (
                  <span className="font-medium">
                    <span className="text-primary">{selectedDishes.length} platos</span>
                    <span className="text-muted-foreground"> · {totalRations} raciones</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground text-xs">Selecciona platos para generar la previsión</span>
                )}
              </div>
              <Button
                onClick={handleForecast}
                disabled={forecasting || selectedDishes.length === 0}
                className="rounded-full shrink-0 gap-2"
                data-testid="generate-forecast-btn"
              >
                {forecasting
                  ? <><Loader2 className="w-4 h-4 animate-spin" />Calculando...</>
                  : <><Sparkles className="w-4 h-4" />Generar previsión</>
                }
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── History mode: Period selector ────────────────────────── */}
      {forecastMode === "history" && (
        <HistoryPeriodSelector
          currentRestaurant={currentRestaurant}
          period={period}
          onForecast={(dateFrom, dateTo, summary) => handleHistoryForecast(dateFrom, dateTo, summary)}
          forecasting={forecasting}
        />
      )}

      {/* ── Results ─────────────────────────────────────────────────── */}
      <div id="forecast-results" className="space-y-5">
        {forecast ? (
          <>
            {/* Summary card with dish thumbnails */}
            <Card className="border-none shadow-soft bg-gradient-to-r from-primary/5 to-transparent">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
                      <TrendingUp className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-heading font-semibold">
                        {forecast.summary || `Previsión · ${periodLabel(period)}`}
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="secondary">{periodLabel(period)}</Badge>
                        {forecast.based_on_history && (
                          <Badge variant="secondary" className="gap-1">
                            <History className="w-3 h-3" /> Historial
                          </Badge>
                        )}
                        <span className="text-sm text-muted-foreground">
                          {displayIngredients.length} ingredientes
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-3xl font-bold text-primary font-mono">
                      {formatCurrency(forecast.total_estimated_cost || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">Coste estimado</div>
                  </div>
                </div>

                {/* Dish thumbnails row */}
                {forecastDishesSnapshot.length > 0 && (
                  <div className="border-t border-border/40 pt-3">
                    <p className="text-xs text-muted-foreground mb-2">Platos incluidos en esta previsión:</p>
                    <div className="flex flex-wrap gap-2">
                      {forecastDishesSnapshot.map(sd => (
                        <div key={sd.dish_id} className="flex items-center gap-1.5 bg-background rounded-full border border-border pl-0.5 pr-3 py-0.5 shadow-sm">
                          {sd.image_url ? (
                            <img src={sd.image_url} alt={sd.name} className="w-6 h-6 rounded-full object-cover shrink-0" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <UtensilsCrossed className="w-3 h-3 text-primary/50" />
                            </div>
                          )}
                          <span className="text-xs font-medium max-w-[100px] truncate">{sd.name}</span>
                          <span className="text-xs text-muted-foreground font-mono">×{sd.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Restock Alerts */}
            <RestockAlerts alerts={forecast.restock_alerts} />

            {/* Action buttons */}
            {displayIngredients.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => setListDialogOpen(true)}
                  variant="outline"
                  data-testid="create-list-btn"
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Crear lista de compra
                </Button>
              </div>
            )}

            {/* Ingredients table */}
            <Card className="border-none shadow-soft">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <CardTitle className="font-heading flex items-center gap-2 text-base">
                    <Package className="w-5 h-5 text-primary" />
                    Ingredientes necesarios
                    {selectedIngs.size > 0 && (
                      <Badge variant="secondary" className="text-xs">{selectedIngs.size} seleccionados</Badge>
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span>Paso:</span>
                      <Input
                        type="number"
                        min="0.001"
                        step="0.1"
                        value={adjustStep}
                        onChange={e => setAdjustStep(Math.max(0.001, parseFloat(e.target.value) || 0.1))}
                        className="w-20 h-7 text-xs text-center font-mono"
                        data-testid="adjust-step-input"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline"
                        className="h-7 px-3 text-sm font-bold border-destructive/50 text-destructive hover:bg-destructive/10"
                        onClick={() => applyAdjustment(-1)} data-testid="adjust-minus-btn">
                        <Minus className="w-3 h-3 mr-1" />{adjustStep}
                      </Button>
                      <Button size="sm" variant="outline"
                        className="h-7 px-3 text-sm font-bold border-primary/50 text-primary hover:bg-primary/10"
                        onClick={() => applyAdjustment(+1)} data-testid="adjust-plus-btn">
                        <Plus className="w-3 h-3 mr-1" />{adjustStep}
                      </Button>
                    </div>
                    {Object.keys(adjustments).length > 0 && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-muted-foreground"
                        onClick={() => setAdjustments({})} data-testid="adjust-reset-btn">
                        <X className="w-3 h-3 mr-1" />Resetear
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedIngs.size > 0
                    ? `Ajuste aplicará a ${selectedIngs.size} ingrediente(s) seleccionado(s)`
                    : "Marca filas para ajustar solo los seleccionados; sin selección aplica a todos"
                  }
                </p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead className="w-10">
                          <Checkbox
                            checked={selectedIngs.size === displayIngredients.length && displayIngredients.length > 0}
                            onCheckedChange={toggleAllIngs}
                            data-testid="select-all-ings"
                          />
                        </TableHead>
                        <TableHead>Ingrediente</TableHead>
                        <TableHead className="text-right">Necesario</TableHead>
                        <TableHead className="text-right">Con merma</TableHead>
                        <TableHead className="text-right">A comprar</TableHead>
                        <TableHead className="text-right">Coste est.</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {displayIngredients.map((ing, rowIdx) => {
                        const isSelected = selectedIngs.has(rowIdx);
                        const hasAdj = ing.adjustment !== 0;
                        return (
                          <TableRow
                            key={rowIdx}
                            className={isSelected ? "bg-primary/5" : ""}
                            data-testid={`ing-row-${rowIdx}`}
                          >
                            <TableCell>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleIngSelection(rowIdx)}
                                data-testid={`ing-check-${rowIdx}`}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{ing.name}</TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {ing.display_quantity} {ing.unit}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-muted-foreground">
                              {ing.display_with_waste} {ing.unit}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <Badge variant={ing.display_to_purchase > 0 ? "destructive" : "secondary"}>
                                  {ing.display_to_purchase} {ing.unit}
                                </Badge>
                                {hasAdj && (
                                  <span className={`text-xs font-mono ${ing.adjustment > 0 ? "text-primary" : "text-destructive"}`}>
                                    {ing.adjustment > 0 ? "+" : ""}{ing.adjustment}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {formatCurrency(ing.estimated_cost || 0)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Historical dishes */}
            {forecast.historical_data?.length > 0 && (
              <Card className="border-none shadow-soft">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <History className="w-4 h-4 text-primary" />
                    Platos del historial
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Plato</TableHead>
                        <TableHead className="text-right">Vendidos (30d)</TableHead>
                        <TableHead className="text-right">Previsión período</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {forecast.historical_data.slice(0, 10).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {(() => {
                                const dish = dishes.find(d => d.name === item.dish_name);
                                return dish?.image_url ? (
                                  <img src={dish.image_url} alt={dish.name || "Plato"} className="w-7 h-7 rounded-lg object-cover shrink-0" />
                                ) : (
                                  <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center shrink-0">
                                    <UtensilsCrossed className="w-3.5 h-3.5 text-muted-foreground" />
                                  </div>
                                );
                              })()}
                              <span className="font-medium">{item.dish_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{item.historical_total}</TableCell>
                          <TableCell className="text-right font-mono text-primary">{item.expected_for_period}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Purchase recommendations */}
            {forecast.purchase_recommendations?.length > 0 && (
              <Card className="border-none shadow-soft">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-primary" />
                    Recomendaciones de compra
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {forecast.purchase_recommendations.map((rec, idx) => (
                    <div key={idx} className="p-3 bg-muted/50 rounded-lg flex items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-sm">{rec.supplier || "Proveedor general"}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {Array.isArray(rec.items) ? rec.items.join(", ") : rec.items}
                        </div>
                      </div>
                      <Badge variant={rec.priority === "alta" ? "destructive" : "secondary"}>{rec.priority}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Alerts */}
            {forecast.alerts?.length > 0 && (
              <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-900/20">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-base flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                    <AlertCircle className="w-4 h-4" />
                    Alertas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1.5">
                    {forecast.alerts.map((alert, idx) => (
                      <li key={idx} className="text-sm text-yellow-700 dark:text-yellow-400">• {alert}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card className="border-dashed border-2">
            <CardContent className="py-16 text-center">
              <Sparkles className="w-14 h-14 mx-auto mb-5 text-muted-foreground/30" />
              <h3 className="font-heading text-xl font-semibold mb-2">Sin previsión activa</h3>
              <p className="text-muted-foreground max-w-xs mx-auto text-sm">
                {forecastMode === "manual"
                  ? "Selecciona los platos del menú y pulsa Generar previsión"
                  : "Pulsa Generar previsión para usar el historial de ventas"
                }
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── History (collapsible) ───────────────────────────────────── */}
      <ForecastHistory
        savedForecasts={savedForecasts}
        historyOpen={historyOpen}
        loading={loadingHistory}
        periodLabel={periodLabel}
        onToggle={() => setHistoryOpen(o => !o)}
        onLoad={loadSavedForecast}
        onDelete={deleteHistory}
      />

      {/* ── Shopping list dialog ────────────────────────────────────── */}
      <Dialog open={listDialogOpen} onOpenChange={setListDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear lista de compra</DialogTitle>
            <DialogDescription>Guarda esta previsión como lista de compra</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre de la lista</Label>
              <Input
                placeholder="Ej: Compra semanal, Pedido fin de semana..."
                value={listName}
                onChange={e => setListName(e.target.value)}
                data-testid="list-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={listType} onValueChange={setListType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="shopping">
                    <span className="flex items-center gap-2"><ShoppingCart className="w-4 h-4" />Lista de compra directa</span>
                  </SelectItem>
                  <SelectItem value="order">
                    <span className="flex items-center gap-2"><Package className="w-4 h-4" />Pedido a proveedor</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg space-y-1.5 text-sm">
              {(() => {
                const toBuy = displayIngredients.filter(i => i.display_to_purchase > 0);
                const covered = displayIngredients.length - toBuy.length;
                return (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-destructive inline-block" />
                        <strong className="text-destructive">{toBuy.length}</strong> a reponer (se incluirán)
                      </span>
                      <span className="font-medium text-primary">
                        {formatCurrency(toBuy.reduce((s, i) => s + (i.estimated_cost || 0), 0))}
                      </span>
                    </div>
                    {covered > 0 && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <span className="w-2 h-2 rounded-full bg-secondary border inline-block" />
                        {covered} con stock suficiente (no se incluirán)
                      </div>
                    )}
                    {Object.keys(adjustments).length > 0 && (
                      <Badge variant="secondary" className="text-xs">Cantidades ajustadas manualmente</Badge>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setListDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateShoppingList} disabled={creatingList} data-testid="create-list-confirm-btn">
              {creatingList
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</>
                : <><Check className="w-4 h-4 mr-2" />Crear lista</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
